import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, X, Upload, Pencil, Trash2, CreditCard, ExternalLink } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import CommitmentSigner from '../components/CommitmentSigner';
import { PaymentMatrix } from '../components/PaymentMatrix';
import { DistributionOrder } from '../components/DistributionOrder';

const PAY_STATUS = {
  pending: { label: 'Pendiente', cls: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  verified: { label: 'Validado', cls: 'bg-green-100 text-green-700 border-green-200' },
  rejected: { label: 'Rechazado', cls: 'bg-red-100 text-red-700 border-red-200' },
};

export default function TandaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [tanda, setTanda] = useState(null);
  const [membersData, setMembersData] = useState({ members: [], total_cycles: 1, elapsed_cycles: 0 });
  const [myStatus, setMyStatus] = useState(null);
  const [myPayments, setMyPayments] = useState([]);
  const [needLevel, setNeedLevel] = useState([50]);
  const [uploading, setUploading] = useState(false);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState('1');

  const fetchData = async () => {
    try {
      const [tandaRes, membersRes, statusRes] = await Promise.all([
        api.get(`/tandas/${id}`),
        api.get(`/tandas/${id}/members`),
        api.get(`/tandas/${id}/my-status`),
      ]);
      setTanda(tandaRes.data);
      setMembersData(membersRes.data);
      setMyStatus(statusRes.data);

      if (statusRes.data?.status === 'approved') {
        const { data: pays } = await api.get(`/tandas/${id}/my-payments`);
        setMyPayments(pays);
        const used = pays.filter(p => p.status !== 'rejected').map(p => p.week_number);
        let next = 1;
        while (used.includes(next)) next++;
        setSelectedCycle(String(Math.min(next, membersRes.data.total_cycles || 1)));
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar la tanda');
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  const handleSignedAndJoined = async (pdfUrl) => {
    try {
      await api.post(`/tandas/${id}/join`, { need_level: needLevel[0], commitment_pdf_url: pdfUrl });
      toast.success('Solicitud enviada con éxito');
      setJoinDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Error al unirse: ' + (error.response?.data?.detail || ''));
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const { data: sig } = await api.get('/tandas/cloudinary-signature');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: 'POST', body: formData });
      const cloudData = await res.json();
      if (!cloudData.secure_url) throw new Error('Cloudinary upload failed');

      await api.post('/tandas/payments', {
        tanda_id: id,
        amount: tanda.contribution_amount,
        proof_url: cloudData.secure_url,
        week_number: parseInt(selectedCycle),
      });

      toast.success(`Comprobante del ciclo ${selectedCycle} enviado para validación`);
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error('Error al subir comprobante');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleApprove = async (userId, approve) => {
    try {
      await api.post(`/tandas/approve-member?tanda_id=${id}&user_id=${userId}&approve=${approve}`);
      toast.success(approve ? 'Miembro aprobade' : 'Solicitud rechazada');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDeleteTanda = async () => {
    try {
      await api.delete(`/tandas/${id}`);
      toast.success('Tanda eliminada');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Error al eliminar la tanda');
    }
  };

  if (!tanda) return <div className="p-8 text-center animate-pulse">Cargando detalles...</div>;

  const isAdmin = user?.role === 'admin';
  const isMember = myStatus && myStatus.status === 'approved';
  const isPending = myStatus && myStatus.status === 'pending';
  const { members, total_cycles, elapsed_cycles } = membersData;
  const cycleOptions = Array.from({ length: Math.min(total_cycles, 104) }, (_, i) => i + 1);

  return (
    <div className="space-y-8" data-testid="tanda-detail-page">
      {/* Admin actions */}
      {isAdmin && (
        <div className="flex flex-wrap gap-3 justify-end" data-testid="admin-tanda-actions">
          <Button variant="outline" data-testid="edit-tanda-detail-btn" className="rounded-full gap-2" onClick={() => navigate(`/admin/edit-tanda/${id}`)}>
            <Pencil size={16} /> Editar Tanda
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="delete-tanda-detail-btn" className="rounded-full gap-2 text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 size={16} /> Eliminar Tanda
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar "{tanda.name}"?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción es irreversible. Se eliminarán también las solicitudes y comprobantes asociados.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cancel-delete-detail">Cancelar</AlertDialogCancel>
                <AlertDialogAction data-testid="confirm-delete-detail" className="bg-red-600 hover:bg-red-700" onClick={handleDeleteTanda}>
                  Sí, eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-8">
        {/* Left Column: Info & Actions */}
        <div className="md:col-span-2 space-y-6">
          <Card className="border-l-4 border-l-primary shadow-lg">
            <CardHeader>
              <Badge className="w-fit mb-2" data-testid="tanda-area-badge">{tanda.area}</Badge>
              <CardTitle className="text-3xl font-bold" data-testid="tanda-title">{tanda.name}</CardTitle>
              <CardDescription>{tanda.description}</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Monto a Recibir</p>
                <p className="text-2xl font-bold">${tanda.total_amount}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Tu Aportación</p>
                <p className="text-xl font-bold">${tanda.contribution_amount}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Frecuencia</p>
                <p className="font-medium">{tanda.periodicity}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Participantes</p>
                <p className="font-medium">{tanda.members?.length || 0} / {tanda.max_participants}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Ciclos Totales</p>
                <p className="font-medium">{total_cycles}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Ciclo Actual</p>
                <p className="font-medium">{elapsed_cycles === 0 ? 'No ha iniciado' : `${elapsed_cycles} de ${total_cycles}`}</p>
              </div>
            </CardContent>
          </Card>

          {/* Join Section (oculta para admins) */}
          {isAdmin && (!myStatus || myStatus.status === 'not_joined') ? null : !myStatus || myStatus.status === 'not_joined' ? (
            <Card>
              <CardHeader>
                <CardTitle>Únete a la Tanda</CardTitle>
                <CardDescription>Indica tu necesidad y firma tu compromiso.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <Label>Nivel de Necesidad (0-100)</Label>
                    <span className="font-bold text-primary" data-testid="need-level-value">{needLevel}%</span>
                  </div>
                  <Slider data-testid="need-level-slider" value={needLevel} onValueChange={setNeedLevel} max={100} step={1} className="py-4" />
                  <p className="text-xs text-muted-foreground">Mayor necesidad = Mayor prioridad en la asignación.</p>
                </div>

                <Dialog open={joinDialogOpen} onOpenChange={setJoinDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="join-tanda-btn" className="w-full rounded-full">Solicitar Unirme</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh]">
                    <DialogHeader>
                      <DialogTitle>Firma de Contrato</DialogTitle>
                      <DialogDescription>
                        Para completar tu solicitud, debes firmar el contrato de adhesión y subir tu INE.
                      </DialogDescription>
                    </DialogHeader>
                    <CommitmentSigner onSigned={handleSignedAndJoined} tanda={tanda} />
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          ) : (
            <div className="bg-muted p-4 rounded-xl text-center" data-testid="membership-status-banner">
              {isPending && <p className="text-yellow-600 font-medium">Tu solicitud está pendiente de aprobación.</p>}
              {isMember && <p className="text-green-600 font-bold flex items-center justify-center gap-2"><Check /> Eres parte de esta tanda.</p>}
              {myStatus?.status === 'rejected' && <p className="text-red-600 font-medium">Tu solicitud fue rechazada.</p>}
            </div>
          )}

          {/* Stripe payment link */}
          {isMember && tanda.stripe_payment_link && (
            <Card className="border-primary/40">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl"><CreditCard className="text-primary" /> Pago en Línea</CardTitle>
                <CardDescription>Paga tu aportación con la liga oficial de Stripe de esta tanda y después sube tu comprobante.</CardDescription>
              </CardHeader>
              <CardContent>
                <a href={tanda.stripe_payment_link} target="_blank" rel="noopener noreferrer" data-testid="stripe-payment-link">
                  <Button className="w-full rounded-full gap-2">Pagar con Stripe <ExternalLink size={16} /></Button>
                </a>
              </CardContent>
            </Card>
          )}

          {/* Payment Upload (Only for members) */}
          {isMember && (
            <Card>
              <CardHeader>
                <CardTitle>Subir Comprobante de Pago</CardTitle>
                <CardDescription>Selecciona el ciclo que estás pagando y sube tu comprobante.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Ciclo de pago</Label>
                  <Select value={selectedCycle} onValueChange={setSelectedCycle}>
                    <SelectTrigger data-testid="payment-cycle-select" className="w-48"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {cycleOptions.map(c => (
                        <SelectItem key={c} value={String(c)}>Ciclo {c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="border-2 border-dashed border-border rounded-xl p-8 text-center space-y-4 hover:bg-muted/50 transition-colors">
                  <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
                  <div className="space-y-2">
                    <Label htmlFor="proof" className="cursor-pointer text-primary hover:underline">
                      Selecciona tu archivo
                    </Label>
                    <Input id="proof" data-testid="payment-proof-input" type="file" className="hidden" onChange={handleFileUpload} accept="image/*" disabled={uploading} />
                    <p className="text-xs text-muted-foreground">Imágenes JPG, PNG — Aportación: ${tanda.contribution_amount}</p>
                  </div>
                  {uploading && <p className="text-sm animate-pulse">Subiendo...</p>}
                </div>

                {/* Payment history */}
                {myPayments.length > 0 && (
                  <div className="space-y-2 pt-2" data-testid="my-payments-history">
                    <Label>Mis comprobantes</Label>
                    {myPayments.map(p => (
                      <div key={p._id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 text-sm">
                        <div className="flex items-center gap-3">
                          <span className="font-medium">Ciclo {p.week_number}</span>
                          <span className="text-muted-foreground">${p.amount}</span>
                          <a href={p.proof_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline text-xs">Ver</a>
                        </div>
                        <Badge variant="outline" className={PAY_STATUS[p.status]?.cls}>{PAY_STATUS[p.status]?.label || p.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column: Participants */}
        <div className="space-y-6">
          <Card data-testid="participants-card">
            <CardHeader>
              <CardTitle className="text-xl">Participantes</CardTitle>
              <CardDescription>Ordenados por prioridad de necesidad</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {members.map((member, index) => (
                  <div key={member._id} data-testid={`member-row-${member.user_id}`} className="p-3 rounded-lg bg-muted/50 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 shrink-0 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                          {index + 1}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{member.full_name}</p>
                          <p className="text-xs text-muted-foreground">Necesidad: {member.need_level}%</p>
                        </div>
                      </div>
                      {isAdmin && member.status === 'pending' && (
                        <div className="flex gap-1 shrink-0">
                          <Button size="icon" variant="ghost" data-testid={`approve-member-${member.user_id}`} className="h-8 w-8 text-green-500" onClick={() => handleApprove(member.user_id, true)}><Check size={16} /></Button>
                          <Button size="icon" variant="ghost" data-testid={`reject-member-${member.user_id}`} className="h-8 w-8 text-red-500" onClick={() => handleApprove(member.user_id, false)}><X size={16} /></Button>
                        </div>
                      )}
                      {isAdmin && member.status !== 'pending' && (
                        <Badge variant="outline" className={member.status === 'approved' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'}>
                          {member.status === 'approved' ? 'Aprobade' : 'Rechazade'}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs pl-11">
                      <span className="font-semibold text-foreground" data-testid={`member-paid-${member.user_id}`}>${member.total_paid} aportado</span>
                      <span className="text-muted-foreground">({member.verified_count}/{total_cycles} ciclos)</span>
                      {member.status === 'approved' && (
                        member.cycles_due > 0 ? (
                          <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200" data-testid={`member-due-${member.user_id}`}>
                            {member.cycles_due} pago{member.cycles_due > 1 ? 's' : ''} pendiente{member.cycles_due > 1 ? 's' : ''}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200">Al corriente</Badge>
                        )
                      )}
                      {isAdmin && member.pending_review_count > 0 && (
                        <Badge variant="outline" className="bg-yellow-100 text-yellow-700 border-yellow-200">
                          {member.pending_review_count} por validar
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
                {members.length === 0 && <p className="text-center text-muted-foreground text-sm">Aún no hay participantes.</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Admin-only: Matriz de pagos y Orden de distribución */}
      {isAdmin && (
        <div className="space-y-8" data-testid="admin-analytics-section">
          <PaymentMatrix members={members} totalCycles={total_cycles} elapsedCycles={elapsed_cycles} tandaId={id} />
          <DistributionOrder tandaId={id} refreshKey={members.length + JSON.stringify(members.map(m => m.verified_count))} />
        </div>
      )}
    </div>
  );
}
